import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/reducers';
import { fetchUsersRequest } from '../store/users/actions';
import { fetchDecisionsRequest, reassignDecisionsRequest } from '../store/decisions/actions';
import { toast } from 'react-toastify';
import { ArrowRight, RefreshCw } from 'lucide-react';
import './AdminReassignation.css';

const statutLabels: Record<string, { label: string; color: string; bg: string }> = {
  brouillon:  { label: 'Brouillon',   color: '#6B7280', bg: '#F3F4F6' },
  a_verifier: { label: 'À vérifier',  color: '#C4A535', bg: '#FFF8E7' },
  valide:     { label: 'Validé',       color: '#047857', bg: '#ECFDF5' },
  anonymise:  { label: 'Anonymisé',    color: '#2563EB', bg: '#EFF6FF' },
  publie:     { label: 'Publié',       color: '#059669', bg: '#D1FAE5' },
};

const AdminReassignation: React.FC = () => {
  const dispatch = useDispatch();
  const { users } = useSelector((state: RootState) => state.users);
  const { decisions, isReassigning } = useSelector((state: RootState) => state.decisions);

  const [fromAgent, setFromAgent] = useState('');
  const [toAgent, setToAgent] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [motif, setMotif] = useState('');

  useEffect(() => {
    dispatch(fetchUsersRequest({ limit: 200 }));
  }, [dispatch]);

  // When source agent changes, fetch their decisions
  useEffect(() => {
    if (fromAgent) {
      dispatch(fetchDecisionsRequest({ agent: fromAgent, page_size: 100 }));
      setSelectedIds([]);
    }
  }, [fromAgent, dispatch]);

  const activeAgents = users.filter(u =>
    u.is_active && (u.role || []).some(r => ['agent', 'validateur', 'admin', 'superadmin'].includes(r.name))
  );

  const agentDecisions = decisions.filter(d => d.agent?.id === fromAgent);

  const getAgentLabel = (u: any) => {
    const name = u.first_name || u.last_name
      ? `${u.first_name || ''} ${u.last_name || ''}`.trim()
      : u.email;
    const role = (u.role || [])[0]?.name || '';
    const roleLabel = role === 'admin' || role === 'superadmin' ? 'Admin'
      : role === 'validateur' ? 'Superviseur'
      : role === 'agent' ? 'Agent' : role;
    return `${name} — ${roleLabel}`;
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const deselectAll = () => setSelectedIds([]);

  const handleTransfer = () => {
    if (!fromAgent || !toAgent || selectedIds.length === 0) {
      toast.error('Sélectionnez les agents et au moins un dossier.');
      return;
    }
    if (fromAgent === toAgent) {
      toast.error('Les deux agents doivent être différents.');
      return;
    }

    dispatch(reassignDecisionsRequest(
      {
        source_agent_id: fromAgent,
        dest_agent_id: toAgent,
        decision_ids: selectedIds,
        motif: motif || undefined,
      },
      () => {
        toast.success(`${selectedIds.length} dossier(s) transféré(s) avec succès.`);
        setSelectedIds([]);
        setMotif('');
        // Refresh the agent's decisions
        dispatch(fetchDecisionsRequest({ agent: fromAgent, page_size: 100 }));
      }
    ));
  };

  return (
    <div className="admin-reassign">
      {/* Agent selection row */}
      <div className="admin-reassign-agents">
        <div className="admin-reassign-agent-col">
          <label className="admin-reassign-label">DEPUIS L'AGENT</label>
          <select
            value={fromAgent}
            onChange={(e) => setFromAgent(e.target.value)}
            className="admin-reassign-select"
          >
            <option value="">Sélectionner un agent...</option>
            {activeAgents.map(u => (
              <option key={u.id} value={u.id}>{getAgentLabel(u)}</option>
            ))}
          </select>
        </div>

        <div className="admin-reassign-arrow">
          <ArrowRight size={22} />
        </div>

        <div className="admin-reassign-agent-col">
          <label className="admin-reassign-label">VERS L'AGENT</label>
          <select
            value={toAgent}
            onChange={(e) => setToAgent(e.target.value)}
            className="admin-reassign-select"
          >
            <option value="">Sélectionner un agent...</option>
            {activeAgents.filter(u => u.id !== fromAgent).map(u => (
              <option key={u.id} value={u.id}>{getAgentLabel(u)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Content area */}
      <div className="admin-reassign-content">
        {/* Dossiers list */}
        <div className="admin-reassign-dossiers">
          {fromAgent ? (
            <>
              <div className="admin-reassign-dossiers-header">
                <div>
                  <h3>
                    Dossiers de {activeAgents.find(u => u.id === fromAgent)
                      ? `${activeAgents.find(u => u.id === fromAgent)!.first_name || ''} ${activeAgents.find(u => u.id === fromAgent)!.last_name || ''}`.trim()
                      : 'l\'agent'}
                  </h3>
                  <span className="admin-reassign-dossiers-count">
                    {agentDecisions.length} dossier(s) — {selectedIds.length} sélectionné(s)
                  </span>
                </div>
                {selectedIds.length > 0 && (
                  <button className="admin-reassign-deselect" onClick={deselectAll}>
                    Tout désélectionner
                  </button>
                )}
              </div>

              <div className="admin-reassign-dossiers-list">
                {agentDecisions.length === 0 ? (
                  <p className="admin-reassign-empty">Aucun dossier pour cet agent.</p>
                ) : (
                  agentDecisions.map(d => {
                    const s = statutLabels[d.statut] || { label: d.statut, color: '#666', bg: '#f3f4f6' };
                    return (
                      <div
                        key={d.id}
                        className={`admin-reassign-dossier ${selectedIds.includes(d.id) ? 'dossier-selected' : ''}`}
                        onClick={() => toggleSelect(d.id)}
                      >
                        <div className="admin-reassign-dossier-left">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(d.id)}
                            onChange={() => toggleSelect(d.id)}
                            className="admin-reassign-check"
                          />
                          <div>
                            <span className="admin-reassign-dossier-num">{d.numero || '—'}</span>
                            <span className="admin-reassign-dossier-info">
                              {d.juridiction || '—'} · {d.nature_litige || d.matiere || '—'}
                            </span>
                          </div>
                        </div>
                        <span
                          className="admin-reassign-dossier-badge"
                          style={{ color: s.color, background: s.bg }}
                        >
                          {s.label}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <div className="admin-reassign-placeholder">
              Sélectionnez un agent source pour voir ses dossiers.
            </div>
          )}
        </div>

        {/* Confirmation panel */}
        <div className="admin-reassign-confirm">
          <h3>
            <RefreshCw size={16} /> Confirmation
          </h3>

          <div className="admin-reassign-confirm-stat">
            <label>À transférer</label>
            <span className="admin-reassign-confirm-count">{selectedIds.length}</span>
            <span className="admin-reassign-confirm-sub">dossier(s) sélectionné(s)</span>
          </div>

          <div className="admin-reassign-confirm-motif">
            <label>Motif (optionnel)</label>
            <textarea
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              placeholder="Ex. surcharge de l'agent source..."
              rows={3}
            />
          </div>

          <button
            className="admin-reassign-btn-transfer"
            onClick={handleTransfer}
            disabled={selectedIds.length === 0 || !toAgent || isReassigning}
          >
            {isReassigning ? 'Transfert en cours...' : `Transférer (${selectedIds.length})`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminReassignation;
